import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { DataGrid, GridActionsCellItem, GridRowModes, GridRowEditStopReasons, GridColDef } from '@mui/x-data-grid';
import { apiUrl } from '../utils/api';
import { formatCurrency } from '../utils/numberFormatter';
import {
  TextField,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Chip,
  Tab,
  Tabs,
  IconButton,
  AlertColor,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const UnitRate = ({ access = 'none', projectId = null, deleteAllAccess = 'none', deleteAllItemsAccess = 'none' }) => {
  const readOnly = access !== 'write';
  const canDeleteAll = deleteAllAccess === 'write';
  const canDeleteAllItems = deleteAllItemsAccess === 'write';
  const [tabValue, setTabValue] = useState(0);

  // Ref to track cancelled rows to prevent processRowUpdate from running
  const cancelledRowsRef = useRef(new Set());

  // Rate Sheets state
  const [rateSheets, setRateSheets] = useState([]);
  const [selectedRateSheet, setSelectedRateSheet] = useState(null);
  const [newRateSheet, setNewRateSheet] = useState({
    sheet_name: '',
    currency_code: 'SGD',
    is_active: true,
    project_id: projectId,
    valid_from: null,
    valid_to: null
  });

  // Rate Sheet Items state
  const [rateSheetItems, setRateSheetItems] = useState([]);
  const [rateSheetDraftItems, setRateSheetDraftItems] = useState([]);
  const [budgetLineItems, setBudgetLineItems] = useState([]);
  const [rateSheetRowModesModel, setRateSheetRowModesModel] = useState({});
  const [rateSheetItemRowModesModel, setRateSheetItemRowModesModel] = useState({});

  // Debug state changes
  useEffect(() => {
    console.log('🔍 rateSheetItems state changed:', rateSheetItems);
  }, [rateSheetItems]);

  useEffect(() => {
    console.log('🔍 rateSheetDraftItems state changed:', rateSheetDraftItems);
  }, [rateSheetDraftItems]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Company data for number formatting
  const [companyData, setCompanyData] = useState({
    number_format: '1,234.56'
  });

  const [deleteRateSheetDialogOpen, setDeleteRateSheetDialogOpen] = useState(false);
  const [rateSheetToDelete, setRateSheetToDelete] = useState(null);
  const [deleteRateItemDialogOpen, setDeleteRateItemDialogOpen] = useState(false);
  const [rateItemToDelete, setRateItemToDelete] = useState(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [duplicatingRateSheetId, setDuplicatingRateSheetId] = useState(null);
  const [importSourceOpen, setImportSourceOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mapFile, setMapFile] = useState<File | null>(null);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const RATE_SHEET_FIELDS = ['Sheet Name', 'Currency Code', 'Active', 'Valid From', 'Valid To'];
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [excelImporting, setExcelImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importSummaryOpen, setImportSummaryOpen] = useState(false);
  const [importSummaryData, setImportSummaryData] = useState<any>(null);
  const fileInputRef = useRef(null);
  // Rate Sheet Items import/export/delete all state
  const [deleteAllItemsDialogOpen, setDeleteAllItemsDialogOpen] = useState(false);
  const [deletingAllItems, setDeletingAllItems] = useState(false);
  const [importItemsSourceOpen, setImportItemsSourceOpen] = useState(false);
  const [mappingItemsOpen, setMappingItemsOpen] = useState(false);
  const [mapItemsFile, setMapItemsFile] = useState<File | null>(null);
  const [fileItemsHeaders, setFileItemsHeaders] = useState<string[]>([]);
  const RATE_SHEET_ITEM_FIELDS = ['Item Description', 'Unit Price', 'Notes'];
  const [mappingItems, setMappingItems] = useState<Record<string, string>>({});
  const [excelItemsImporting, setExcelItemsImporting] = useState(false);
  const [importItemsProgress, setImportItemsProgress] = useState(0);
  const [importItemsSummaryOpen, setImportItemsSummaryOpen] = useState(false);
  const [importItemsSummaryData, setImportItemsSummaryData] = useState<any>(null);
  const fileInputItemsRef = useRef(null);
  const showNotification = useCallback((message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const createDraftItem = useCallback(
    () => ({
      id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      rsi_id: null,
      rate_sheet_id: selectedRateSheet?.rate_sheet_id || null,
      bli_id: '',
      unit_price: null,
      notes: '',
      created_at: null,
      created_by: null,
      isDraft: true
    }),
    [selectedRateSheet]
  );

  const budgetLineItemMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(budgetLineItems)) {
      budgetLineItems.forEach(item => {
        if (item && item.bli_id !== undefined && item.bli_id !== null) {
          map.set(String(item.bli_id), item);
        }
      });
    }
    return map;
  }, [budgetLineItems]);

  const fetchRateSheets = useCallback(async () => {
    if (access === 'none') return;

    setLoading(true);
    try {
      let url = apiUrl('/api/rate-sheets');
      if (projectId) {
        url += `?project_id=${projectId}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch rate sheets');

      const data = await response.json();
      setRateSheets(data);
    } catch (error) {
      console.error('Error fetching rate sheets:', error);
      showNotification('Failed to load rate sheets', 'error');
    } finally {
      setLoading(false);
    }
  }, [access, projectId, showNotification]);

  const fetchRateSheetItems = useCallback(
    async (rateSheetId) => {
      if (!rateSheetId) {
        setRateSheetItemRowModesModel({});
        setRateSheetItems([]);
        setRateSheetDraftItems([]);
        return;
      }

      setItemsLoading(true);
      // Clear existing state first to prevent stale references
      setRateSheetItemRowModesModel({});
      setRateSheetItems([]);
      setRateSheetDraftItems([]);
      
      try {
        const response = await fetch(apiUrl(`/api/rate-sheets/${rateSheetId}/items`));
        if (!response.ok) throw new Error('Failed to fetch rate sheet items');

        const data = await response.json();
        console.log('🔍 Raw API response data:', data);
        
        const normalized = (data || [])
          .filter(item => item && item.rsi_id !== undefined && item.rsi_id !== null)
          .map(item => {
            // Ensure all fields are properly initialized
            const normalizedItem = {
              // Copy all original fields first
              ...item,
              // Override with normalized values
              id: item.rsi_id,
              rsi_id: item.rsi_id,
              rate_sheet_id: item.rate_sheet_id || null,
              bli_id: item.bli_id ? String(item.bli_id) : '',
              unit_price:
                item.unit_price !== undefined && item.unit_price !== null
                  ? Number(item.unit_price)
                  : null,
              notes: item.notes !== undefined && item.notes !== null ? String(item.notes) : '',
              created_at: item.created_at || null,
              created_by: item.created_by || null,
              isDraft: false
            };
            return normalizedItem;
          });

        console.log('🔍 Normalized rate sheet items:', normalized);
        setRateSheetItems(normalized);
        // No automatic draft items - user must click + button to add new items
        setRateSheetDraftItems([]);
        setRateSheetItemRowModesModel({});
      } catch (error) {
        console.error('Error fetching rate sheet items:', error);
        showNotification('Failed to load rate sheet items', 'error');
      } finally {
        setItemsLoading(false);
      }
    },
    [showNotification]
  );

  const fetchBudgetLineItems = useCallback(async () => {
    try {
      let url = apiUrl('/api/budget-line-items?is_active=true');
      if (projectId) {
        url += `&project_id=${projectId}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch budget line items');

      const data = await response.json();
      setBudgetLineItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching budget line items:', error);
    }
  }, [projectId]);

  useEffect(() => {
    fetchRateSheets();
    fetchBudgetLineItems();
  }, [fetchRateSheets, fetchBudgetLineItems]);

  useEffect(() => {
    if (!excelImporting) return;
    const timer = setInterval(() => {
      setImportProgress(p => (p < 90 ? p + 5 : p));
    }, 300);
    return () => clearInterval(timer);
  }, [excelImporting]);

  useEffect(() => {
    if (!excelItemsImporting) return;
    const timer = setInterval(() => {
      setImportItemsProgress(p => (p < 90 ? p + 5 : p));
    }, 300);
    return () => clearInterval(timer);
  }, [excelItemsImporting]);

  useEffect(() => {
    setNewRateSheet(prev => ({ ...prev, project_id: projectId }));
  }, [projectId]);

  // Fetch company data for number formatting
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const projectRes = await fetch(apiUrl(`/api/projects/${projectId}`), {
          credentials: 'include'
        });
        if (!projectRes.ok) return;
        const currentProject = await projectRes.json();
        if (!currentProject.company_id) return;

        const response = await fetch(apiUrl(`/api/companies/${currentProject.company_id}`), {
          credentials: 'include'
        });
        if (!response.ok) return;
        const data = await response.json();
        setCompanyData({ number_format: data.number_format || '1,234.56' });
      } catch (error) {
        console.error('Error fetching company data:', error);
      }
    };
    if (projectId) fetchCompanyData();
  }, [projectId]);

  // Rate Sheet handlers
  const handleAddRateSheet = async () => {
    if (readOnly || !newRateSheet.sheet_name.trim()) return;
    
    try {
      const payload = { ...newRateSheet };
      if (payload.valid_from) {
        payload.valid_from = payload.valid_from.toISOString();
      }
      if (payload.valid_to) {
        payload.valid_to = payload.valid_to.toISOString();
      }
      
      const response = await fetch(apiUrl('/api/rate-sheets'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create rate sheet');
      }
      
      setNewRateSheet({
        sheet_name: '',
        currency_code: 'SGD',
        is_active: true,
        project_id: projectId,
        valid_from: null,
        valid_to: null
      });
      fetchRateSheets();
      showNotification('Rate sheet created successfully');
    } catch (error) {
      console.error('Error creating rate sheet:', error);
      showNotification(error.message, 'error');
    }
  };

  const handleDeleteRateSheetClick = useCallback(
    (id) => () => {
      if (readOnly) return;
      const sheet = rateSheets.find(s => s.rate_sheet_id === id);
      setRateSheetToDelete(sheet);
      setDeleteRateSheetDialogOpen(true);
    },
    [readOnly, rateSheets]
  );

  const handleDeleteRateSheetConfirm = useCallback(
    async () => {
      if (!rateSheetToDelete) return;

      try {
        const id = rateSheetToDelete.rate_sheet_id;
        const response = await fetch(apiUrl(`/api/rate-sheets/${id}`), {
          method: 'DELETE'
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete rate sheet');
        }

        if (selectedRateSheet?.rate_sheet_id === id) {
          setSelectedRateSheet(null);
          setRateSheetItems([]);
          setRateSheetRowModesModel((prev) => {
            const previous = (prev || {}) as Record<string, any>;
            const { [String(id)]: removed, ...rest } = previous;
            return rest;
          });
        }

        fetchRateSheets();
        showNotification('Rate sheet deleted successfully');
        setDeleteRateSheetDialogOpen(false);
        setRateSheetToDelete(null);
      } catch (error) {
        console.error('Error deleting rate sheet:', error);
        showNotification(error.message, 'error');
        setDeleteRateSheetDialogOpen(false);
      }
    },
    [rateSheetToDelete, fetchRateSheets, selectedRateSheet, showNotification]
  );

  const handleDeleteRateSheetCancel = useCallback(() => {
    setDeleteRateSheetDialogOpen(false);
    setRateSheetToDelete(null);
  }, []);

  const handleDuplicateRateSheet = useCallback(
    (id) => async () => {
      if (readOnly) return;

      setDuplicatingRateSheetId(id);
      try {
        // Find the rate sheet to duplicate
        const originalSheet = rateSheets.find(sheet => sheet.rate_sheet_id === id);
        if (!originalSheet) {
          throw new Error('Rate sheet not found');
        }

        // Create the duplicate rate sheet payload
        const duplicateSheetPayload = {
          sheet_name: `${originalSheet.sheet_name} (Copy)`,
          currency_code: originalSheet.currency_code,
          is_active: originalSheet.is_active,
          project_id: originalSheet.project_id,
          valid_from: originalSheet.valid_from,
          valid_to: originalSheet.valid_to
        };

        // Create the new rate sheet
        const sheetResponse = await fetch(apiUrl('/api/rate-sheets'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(duplicateSheetPayload)
        });

        if (!sheetResponse.ok) {
          const errorData = await sheetResponse.json();
          throw new Error(errorData.error || 'Failed to create duplicate rate sheet');
        }

        const newSheet = await sheetResponse.json();
        const newSheetId = newSheet.rate_sheet_id;

        // Fetch the original rate sheet items
        const itemsResponse = await fetch(apiUrl(`/api/rate-sheets/${id}/items`), {
          credentials: 'include'
        });
        if (!itemsResponse.ok) {
          throw new Error('Failed to fetch original rate sheet items');
        }

        const originalItems = await itemsResponse.json();

        // Duplicate each rate sheet item
        for (const item of originalItems) {
          const duplicateItemPayload = {
            rate_sheet_id: newSheetId,
            bli_id: item.bli_id,
            unit_price: item.unit_price,
            notes: item.notes
          };

          const itemResponse = await fetch(apiUrl(`/api/rate-sheets/${newSheetId}/items`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(duplicateItemPayload)
          });

          if (!itemResponse.ok) {
            console.warn(`Failed to duplicate rate sheet item ${item.rsi_id}`);
          }
        }

        // Refresh the rate sheets list and notify user
        await fetchRateSheets();
        showNotification(`Rate sheet duplicated successfully as "${duplicateSheetPayload.sheet_name}"`, 'success');

      } catch (error) {
        console.error('Error duplicating rate sheet:', error);
        showNotification(error.message, 'error');
      } finally {
        setDuplicatingRateSheetId(null);
      }
    },
    [fetchRateSheets, rateSheets, readOnly, showNotification]
  );

  const handleSelectRateSheet = useCallback(
    (rateSheet) => {
      const nextId = rateSheet?.rate_sheet_id ?? null;
      const currentId = selectedRateSheet?.rate_sheet_id ?? null;

      console.log('🔍 Rate sheet selection:', { 
        rateSheet, 
        nextId, 
        currentId, 
        willFetchItems: rateSheet && rateSheet.rate_sheet_id,
        rateSheetItemsCount: rateSheetItems.length
      });

      // Always fetch items even if it's the same rate sheet, 
      // in case the items haven't been loaded yet
      if (nextId === currentId && rateSheetItems.length > 0) {
        console.log('🔍 Same rate sheet selected and items already loaded, skipping');
        return;
      }

      // Clear state in the correct order to prevent DataGrid issues
      setRateSheetItemRowModesModel({});
      setRateSheetItems([]);
      setRateSheetDraftItems([]);
      setSelectedRateSheet(rateSheet || null);
      
      if (rateSheet && rateSheet.rate_sheet_id) {
        console.log('🔍 Fetching items for rate sheet:', rateSheet.rate_sheet_id);
        fetchRateSheetItems(rateSheet.rate_sheet_id);
      }
    },
    [fetchRateSheetItems, selectedRateSheet, rateSheetItems.length]
  );

  const handleRateSheetEditClick = useCallback(
    (id) => () => {
      setRateSheetRowModesModel((prev) => ({
        ...(prev || {}),
        [id]: { mode: GridRowModes.Edit }
      }));
    },
    []
  );

  const handleRateSheetSaveClick = useCallback(
    (id) => () => {
      setRateSheetRowModesModel((prev) => ({
        ...(prev || {}),
        [id]: { mode: GridRowModes.View }
      }));
    },
    []
  );

  const handleRateSheetCancelClick = useCallback(
    (id) => () => {
      setRateSheetRowModesModel((prev) => ({
        ...(prev || {}),
        [id]: { mode: GridRowModes.View, ignoreModifications: true }
      }));
    },
    []
  );

  const handleRateSheetRowModesModelChange = useCallback((model) => {
    setRateSheetRowModesModel(model && typeof model === 'object' ? model : {});
  }, []);

  const handleRateSheetRowEditStop = useCallback((params) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      setRateSheetRowModesModel((prev) => ({
        ...(prev || {}),
        [params.id]: { mode: GridRowModes.View }
      }));
    }
  }, []);

  const handleRateSheetProcessRowUpdateError = useCallback(
    (error) => {
      console.error('Error updating rate sheet:', error);
      showNotification(error.message || 'Failed to update rate sheet', 'error');
    },
    [showNotification]
  );

  const processRateSheetRowUpdate = useCallback(
    async (newRow) => {
      if (readOnly) {
        return newRow;
      }

      const toIsoString = (value) => {
        if (!value) return null;
        if (value instanceof Date) {
          return Number.isNaN(value.getTime()) ? null : value.toISOString();
        }
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
      };

      const payload = {
        sheet_name: newRow.sheet_name ?? '',
        currency_code: newRow.currency_code ?? 'SGD',
        is_active: typeof newRow.is_active === 'boolean' ? newRow.is_active : Boolean(newRow.is_active),
        project_id:
          newRow.project_id === undefined ? selectedRateSheet?.project_id ?? null : newRow.project_id,
        created_by: newRow.created_by ?? null,
        valid_from: toIsoString(newRow.valid_from),
        valid_to: toIsoString(newRow.valid_to)
      };

      try {
        const response = await fetch(apiUrl(`/api/rate-sheets/${newRow.rate_sheet_id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update rate sheet');
        }

        const saved = await response.json();

        setRateSheets((prev) =>
          prev.map((sheet) => (sheet.rate_sheet_id === saved.rate_sheet_id ? saved : sheet))
        );

        if (selectedRateSheet?.rate_sheet_id === saved.rate_sheet_id) {
          setSelectedRateSheet(saved);
        }

        showNotification('Rate sheet updated successfully');
        return saved;
      } catch (error) {
        console.error('Error updating rate sheet:', error);
        showNotification(error.message, 'error');
        throw error;
      }
    },
    [readOnly, selectedRateSheet, showNotification]
  );

  const handleAddDraftItemRow = useCallback(() => {
    if (readOnly) {
      return;
    }
    if (!selectedRateSheet) {
      showNotification('Select a rate sheet first', 'error');
      return;
    }
    const draft = createDraftItem();
    setRateSheetDraftItems((prev) => [...prev, draft]);
    // Set the new draft row to edit mode
    setRateSheetItemRowModesModel((prev) => ({
      ...(prev || {}),
      [draft.id]: { mode: GridRowModes.Edit, fieldToFocus: 'bli_id' }
    }));
  }, [createDraftItem, readOnly, selectedRateSheet, showNotification]);

  const handleRateSheetItemEditClick = useCallback(
    (id) => () => {
      setRateSheetItemRowModesModel((prev) => ({
        ...(prev || {}),
        [id]: { mode: GridRowModes.Edit }
      }));
    },
    []
  );

  const handleRateSheetItemSaveClick = useCallback(
    (id) => () => {
      setRateSheetItemRowModesModel((prev) => ({
        ...(prev || {}),
        [id]: { mode: GridRowModes.View }
      }));
    },
    []
  );

  const handleRateSheetItemCancelClick = useCallback(
    (id, isDraft) => () => {
      // Mark row as cancelled to prevent processRowUpdate from running
      cancelledRowsRef.current.add(id);
      
      // Remove from row modes model
      setRateSheetItemRowModesModel((prev) => {
        const newModel = { ...(prev || {}) };
        delete newModel[id];
        return newModel;
      });
      
      // If it's a draft row, remove it from draft items
      if (isDraft && !readOnly) {
        setRateSheetDraftItems((prev) => prev.filter((row) => row.id !== id));
      }
      
      // Clean up the cancelled flag after a short delay
      setTimeout(() => {
        cancelledRowsRef.current.delete(id);
      }, 100);
    },
    [readOnly]
  );

  const handleRateSheetItemRowModesModelChange = useCallback((model) => {
    setRateSheetItemRowModesModel(model && typeof model === 'object' ? model : {});
  }, []);

  const handleRateSheetItemRowEditStop = useCallback((params) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      setRateSheetItemRowModesModel((prev) => ({
        ...(prev || {}),
        [params.id]: { mode: GridRowModes.View }
      }));
    }
  }, []);

  const handleRateSheetItemProcessRowUpdateError = useCallback(
    (error) => {
      console.error('❌ handleRateSheetItemProcessRowUpdateError called');
      console.error('❌ Error details:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);
      showNotification(error.message || 'Failed to update rate sheet item', 'error');
    },
    [showNotification]
  );

  // Rate Sheet Items handlers
  const handleDeleteRateItemClick = useCallback(
    (id) => () => {
      if (readOnly) return;
      if (!selectedRateSheet) return;
      const item = rateSheetItems.find(i => i.rsi_id === id);
      setRateItemToDelete(item);
      setDeleteRateItemDialogOpen(true);
    },
    [readOnly, selectedRateSheet, rateSheetItems]
  );

  const handleDeleteRateItemConfirm = useCallback(
    async () => {
      if (!rateItemToDelete) return;

      try {
        const response = await fetch(apiUrl(`/api/rate-sheet-items/${rateItemToDelete.rsi_id}`), {
          method: 'DELETE'
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete rate item');
        }

        if (selectedRateSheet) {
          fetchRateSheetItems(selectedRateSheet.rate_sheet_id);
        }
        showNotification('Rate item deleted successfully');
        setDeleteRateItemDialogOpen(false);
        setRateItemToDelete(null);
      } catch (error) {
        console.error('Error deleting rate item:', error);
        showNotification(error.message, 'error');
        setDeleteRateItemDialogOpen(false);
      }
    },
    [rateItemToDelete, fetchRateSheetItems, selectedRateSheet, showNotification]
  );

  const handleDeleteRateItemCancel = useCallback(() => {
    setDeleteRateItemDialogOpen(false);
    setRateItemToDelete(null);
  }, []);

  // Delete all
  const handleDeleteAllClick = () => {
    if (!canDeleteAll) return;
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllConfirm = async () => {
    setDeletingAll(true);
    try {
      let url = apiUrl('/api/rate-sheets/delete-all');
      if (projectId) {
        url += `?project_id=${projectId}`;
      }

      const response = await fetch(url, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete all rate sheets');
      }

      fetchRateSheets();
      setSelectedRateSheet(null);
      setRateSheetItems([]);
      setRateSheetDraftItems([]);
      showNotification('All rate sheets deleted successfully', 'success');
      setDeleteAllDialogOpen(false);
    } catch (error) {
      console.error('Error deleting all rate sheets:', error);
      showNotification(error.message, 'error');
      setDeleteAllDialogOpen(false);
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDeleteAllCancel = () => {
    setDeleteAllDialogOpen(false);
  };

  // Import/Export handlers for Rate Sheets
  const handleDownloadTemplate = () => {
    fetch(apiUrl(`/api/rate-sheets/template?project_id=${projectId}`))
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'rate_sheets_template.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => showNotification('Failed to download template', 'error'));
  };

  const handleExport = () => {
    const XLSX = require('xlsx');
    const rows = rateSheets.map(sheet => ({
      'Sheet Name': sheet.sheet_name || '',
      'Currency Code': sheet.currency_code || '',
      'Active': sheet.is_active ? 'Yes' : 'No',
      'Valid From': sheet.valid_from || '',
      'Valid To': sheet.valid_to || ''
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rate Sheets');
    XLSX.writeFile(wb, 'rate_sheets.xlsx');
  };

  const handleImportClick = () => {
    if (readOnly) return;
    setImportSourceOpen(true);
  };

  const handleImportSource = (isThird: boolean) => {
    setImportSourceOpen(false);
    if (isThird) {
      setMapFile(null);
      setFileHeaders([]);
      setMapping({});
      setMappingOpen(true);
    } else { fileInputRef.current?.click(); }
  };

  const handleMapFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMapFile(f);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows: any = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
      const headers = ((rows as any)[0] || []).map((v: any) => String(v || ''));
      setFileHeaders(headers);
      const auto: Record<string, string> = { ...mapping };
      RATE_SHEET_FIELDS.forEach(fld => {
        const idx = headers.findIndex(h => h.toLowerCase().replace(/\s+/g, '_') === fld.toLowerCase().replace(/\s+/g, '_'));
        if (idx >= 0) auto[fld] = headers[idx];
      });
      setMapping(auto);
    } catch { setFileHeaders([]); }
  };

  const handleMappingSubmit = async () => {
    if (!mapFile) { alert('Select a file'); return; }
    try {
      const buf = await mapFile.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' }) as any[];
      const norm = rows.map(r => RATE_SHEET_FIELDS.reduce((obj, fld) => ({ ...obj, [fld]: (r as any)[mapping[fld]] ?? '' }), {}));
      const newWb = XLSX.utils.book_new();
      const newWs = XLSX.utils.json_to_sheet(norm);
      XLSX.utils.book_append_sheet(newWb, newWs, 'Sheet1');
      const buf2 = XLSX.write(newWb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf2], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const mappedFile = new File([blob], 'mapped.xlsx');
      setMappingOpen(false);
      await runImport(mappedFile);
    } catch (err: any) { alert(err.message || 'Mapping import failed'); }
  };

  const runImport = async (file: File) => {
    try {
      setExcelImporting(true);
      setImportProgress(0);
      const fd = new FormData();
      fd.append('file', file);
      const response = await fetch(apiUrl(`/api/rate-sheets/import?confirm=true&project_id=${projectId}&overwrite=true`), {
        method: 'POST',
        credentials: 'include',
        body: fd
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to import rate sheets');
      const added = data.created || 0;
      const overwritten = data.updated || 0;
      const skipped = data.skipped || 0;
      const failed = (data.errors || []).length;
      const logRows = [
        ...Array(added).fill(null).map((_: any, i: number) => ({ label: `Rate Sheet ${i + 1}`, status: 'Added' })),
        ...Array(overwritten).fill(null).map((_: any, i: number) => ({ label: `Rate Sheet ${added + i + 1}`, status: 'Overwritten' })),
        ...Array(skipped).fill(null).map((_: any, i: number) => ({ label: `Existing Rate Sheet ${i + 1}`, status: 'Skipped' })),
        ...(data.errors || []).map((e: any) => ({ label: `Row ${e.row || '?'}`, status: `Failed: ${e.field ? e.field + ' - ' : ''}${e.error || e}` }))
      ];
      setImportProgress(100);
      setImportSummaryData({ total: added + overwritten + skipped + failed, added, overwritten, skipped, failed, rows: logRows });
      setImportSummaryOpen(true);
      fetchRateSheets();
    } catch (err: any) {
      setImportProgress(0);
      setImportSummaryData({ total: 0, added: 0, overwritten: 0, skipped: 0, failed: 1, error: err.message || 'Import failed', rows: [{ label: 'Import error', status: `Failed: ${err.message || 'Import failed'}` }] });
      setImportSummaryOpen(true);
      showNotification(err.message || 'Import failed', 'error');
    } finally { setExcelImporting(false); }
  };

  const downloadImportLog = (data: any) => {
    const logRows = (data.rows || []).map((r: any, i: number) => {
      const s = r.status || ''
      let status: string, notes: string
      if (s === 'Added') { status = 'Pass'; notes = 'New Item added' }
      else if (s === 'Overwritten') { status = 'Pass'; notes = 'Item overwritten' }
      else if (s === 'Skipped') { status = 'Pass'; notes = 'Existing kept (skipped)' }
      else { status = 'Failed'; notes = s.replace(/^Failed:\s*/i, '') }
      return { row_no: i + 2, rate_sheet: r.label, status, notes }
    })
    fetch(apiUrl('/api/unit-rates/import-log'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows: logRows })
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = 'RateSheet_Import_Log.xlsx'; a.click()
        window.URL.revokeObjectURL(url)
      })
      .catch(() => alert('Failed to generate import log'))
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    runImport(file);
  };

  // Rate Sheet Items Import/Export/Delete All handlers
  const handleDownloadItemsTemplate = () => {
    if (!selectedRateSheet) {
      showNotification('Select a rate sheet first', 'error');
      return;
    }
    fetch(apiUrl(`/api/rate-sheets/${selectedRateSheet.rate_sheet_id}/items/template`), {
      credentials: 'include'
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rate_sheet_items_template_${selectedRateSheet.rate_sheet_id}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => showNotification('Failed to download template', 'error'));
  };

  const handleExportItems = () => {
    if (!selectedRateSheet) {
      showNotification('Select a rate sheet first', 'error');
      return;
    }
    fetch(apiUrl(`/api/rate-sheets/${selectedRateSheet.rate_sheet_id}/items/export`), {
      credentials: 'include'
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rate_sheet_items_${selectedRateSheet.sheet_name}_${selectedRateSheet.rate_sheet_id}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => showNotification('Failed to export items', 'error'));
  };

  const handleImportItemsClick = () => {
    if (readOnly) return;
    if (!selectedRateSheet) {
      showNotification('Select a rate sheet first', 'error');
      return;
    }
    setImportItemsSourceOpen(true);
  };

  const handleImportItemsSource = (isThird: boolean) => {
    setImportItemsSourceOpen(false);
    if (isThird) {
      setMapItemsFile(null);
      setFileItemsHeaders([]);
      setMappingItems({});
      setMappingItemsOpen(true);
    } else { fileInputItemsRef.current?.click(); }
  };

  const handleMapItemsFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMapItemsFile(f);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows: any = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
      const headers = ((rows as any)[0] || []).map((v: any) => String(v || ''));
      setFileItemsHeaders(headers);
      const auto: Record<string, string> = { ...mappingItems };
      RATE_SHEET_ITEM_FIELDS.forEach(fld => {
        const idx = headers.findIndex(h => h.toLowerCase().replace(/\s+/g, '_') === fld.toLowerCase().replace(/\s+/g, '_'));
        if (idx >= 0) auto[fld] = headers[idx];
      });
      setMappingItems(auto);
    } catch { setFileItemsHeaders([]); }
  };

  const handleMappingItemsSubmit = async () => {
    if (!mapItemsFile) { alert('Select a file'); return; }
    try {
      const buf = await mapItemsFile.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' }) as any[];
      const norm = rows.map(r => RATE_SHEET_ITEM_FIELDS.reduce((obj, fld) => ({ ...obj, [fld]: (r as any)[mappingItems[fld]] ?? '' }), {}));
      const newWb = XLSX.utils.book_new();
      const newWs = XLSX.utils.json_to_sheet(norm);
      XLSX.utils.book_append_sheet(newWb, newWs, 'Sheet1');
      const buf2 = XLSX.write(newWb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf2], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const mappedFile = new File([blob], 'mapped.xlsx');
      setMappingItemsOpen(false);
      await runItemsImport(mappedFile);
    } catch (err: any) { alert(err.message || 'Mapping import failed'); }
  };

  const runItemsImport = async (file: File) => {
    if (!selectedRateSheet) { showNotification('Select a rate sheet first', 'error'); return; }
    try {
      setExcelItemsImporting(true);
      setImportItemsProgress(0);
      const fd = new FormData();
      fd.append('file', file);
      const response = await fetch(apiUrl(`/api/rate-sheets/${selectedRateSheet.rate_sheet_id}/items/import?confirm=true&overwrite=true`), {
        method: 'POST',
        credentials: 'include',
        body: fd
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to import items');
      const added = data.created || 0;
      const overwritten = data.updated || 0;
      const skipped = data.skipped || 0;
      const failed = (data.errors || []).length;
      const logRows = [
        ...Array(added).fill(null).map((_: any, i: number) => ({ label: `Item ${i + 1}`, status: 'Added' })),
        ...Array(overwritten).fill(null).map((_: any, i: number) => ({ label: `Item ${added + i + 1}`, status: 'Overwritten' })),
        ...Array(skipped).fill(null).map((_: any, i: number) => ({ label: `Existing Item ${i + 1}`, status: 'Skipped' })),
        ...(data.errors || []).map((e: any) => ({ label: `Row ${e.row || '?'}`, status: `Failed: ${e.field ? e.field + ' - ' : ''}${e.error || e}` }))
      ];
      setImportItemsProgress(100);
      setImportItemsSummaryData({ total: added + overwritten + skipped + failed, added, overwritten, skipped, failed, rows: logRows });
      setImportItemsSummaryOpen(true);
      fetchRateSheetItems(selectedRateSheet.rate_sheet_id);
    } catch (err: any) {
      setImportItemsProgress(0);
      setImportItemsSummaryData({ total: 0, added: 0, overwritten: 0, skipped: 0, failed: 1, error: err.message || 'Import failed', rows: [{ label: 'Import error', status: `Failed: ${err.message || 'Import failed'}` }] });
      setImportItemsSummaryOpen(true);
      showNotification(err.message || 'Import failed', 'error');
    } finally { setExcelItemsImporting(false); }
  };

  const downloadItemsImportLog = (data: any) => {
    const logRows = (data.rows || []).map((r: any, i: number) => {
      const s = r.status || ''
      let status: string, notes: string
      if (s === 'Added') { status = 'Pass'; notes = 'New Item added' }
      else if (s === 'Overwritten') { status = 'Pass'; notes = 'Item overwritten' }
      else if (s === 'Skipped') { status = 'Pass'; notes = 'Existing kept (skipped)' }
      else { status = 'Failed'; notes = s.replace(/^Failed:\s*/i, '') }
      return { row_no: i + 2, item: r.label, status, notes }
    })
    fetch(apiUrl('/api/unit-rate-items/import-log'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows: logRows })
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = 'RateSheetItems_Import_Log.xlsx'; a.click()
        window.URL.revokeObjectURL(url)
      })
      .catch(() => alert('Failed to generate import log'))
  };

  const handleFileItemsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    runItemsImport(file);
  };

  const handleDeleteAllItemsClick = () => {
    if (!canDeleteAllItems) return;
    if (!selectedRateSheet) {
      showNotification('Select a rate sheet first', 'error');
      return;
    }
    setDeleteAllItemsDialogOpen(true);
  };

  const handleDeleteAllItemsConfirm = async () => {
    if (!selectedRateSheet) return;
    setDeletingAllItems(true);
    try {
      const response = await fetch(apiUrl(`/api/rate-sheets/${selectedRateSheet.rate_sheet_id}/items/delete-all`), {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete all items');
      }

      fetchRateSheetItems(selectedRateSheet.rate_sheet_id);
      showNotification('All rate sheet items deleted successfully', 'success');
      setDeleteAllItemsDialogOpen(false);
    } catch (error) {
      console.error('Error deleting all items:', error);
      showNotification(error.message, 'error');
      setDeleteAllItemsDialogOpen(false);
    } finally {
      setDeletingAllItems(false);
    }
  };

  const handleDeleteAllItemsCancel = () => {
    setDeleteAllItemsDialogOpen(false);
  };

  const processRateSheetItemRowUpdate = useCallback(
    async (newRow, oldRow) => {
      console.log('🔄 processRateSheetItemRowUpdate called');
      console.log('📥 newRow:', newRow);
      console.log('📥 oldRow:', oldRow);
      console.log('📥 readOnly:', readOnly);
      console.log('📥 selectedRateSheet:', selectedRateSheet);
      
      // Check if this row was cancelled
      if (cancelledRowsRef.current.has(newRow?.id)) {
        console.log('🚫 Row was cancelled, skipping processRowUpdate');
        return oldRow; // Return the old row to prevent further processing
      }

      // Validate that newRow is a proper object
      if (!newRow || typeof newRow !== 'object') {
        console.error('❌ Invalid newRow - expected object, got:', typeof newRow, newRow);
        console.error('❌ oldRow for reference:', oldRow);
        showNotification('Invalid row data received', 'error');
        
        // Return a safe fallback object
        const safeRow = oldRow && typeof oldRow === 'object' ? { ...oldRow } : {
          id: `error-fallback-${Date.now()}`,
          rsi_id: null,
          rate_sheet_id: selectedRateSheet?.rate_sheet_id || null,
          bli_id: '',
          unit_price: null,
          notes: '',
          created_at: null,
          created_by: null,
          isDraft: true
        };
        console.log('🛡️ Returning safe fallback row:', safeRow);
        return safeRow;
      }

      if (readOnly) {
        console.log('❌ Read-only mode, returning oldRow');
        return oldRow;
      }

      if (!selectedRateSheet) {
        console.log('❌ No selected rate sheet');
        showNotification('Select a rate sheet first', 'error');
        return oldRow;
      }

      const isDraftRow = !newRow.rsi_id;
      const rowId = newRow.id ?? oldRow?.id ?? '';
      const trimmedNotes = newRow.notes ? String(newRow.notes).trim() : '';
      const trimmedBliId = newRow.bli_id ? String(newRow.bli_id) : '';
      const resolvedBliId =
        trimmedBliId || (oldRow && oldRow.bli_id ? String(oldRow.bli_id) : '');
      const numericUnitPrice =
        newRow.unit_price === null || newRow.unit_price === undefined || newRow.unit_price === ''
          ? NaN
          : Number(newRow.unit_price);

      console.log('🔍 Processing data:');
      console.log('   isDraftRow:', isDraftRow);
      console.log('   rowId:', rowId);
      console.log('   trimmedNotes:', trimmedNotes);
      console.log('   trimmedBliId:', trimmedBliId);
      console.log('   resolvedBliId:', resolvedBliId);
      console.log('   numericUnitPrice:', numericUnitPrice);

      if (!resolvedBliId) {
        console.log('❌ BOQ line item is required');
        showNotification('BOQ line item is required', 'error');
        if (rowId) {
          setRateSheetItemRowModesModel((prev) => ({
            ...(prev || {}),
            [rowId]: { mode: GridRowModes.Edit, fieldToFocus: 'bli_id' }
          }));
        }
        // Return old row if available, otherwise reject promise to prevent save
        if (oldRow) {
          return oldRow;
        }
        return Promise.reject(new Error('BOQ line item is required'));
      }

      const meta = budgetLineItemMap.get(resolvedBliId);
      if (!meta && isDraftRow) {
        showNotification('Selected BOQ line item is invalid or unavailable', 'error');
        if (rowId) {
          setRateSheetItemRowModesModel((prev) => ({
            ...(prev || {}),
            [rowId]: { mode: GridRowModes.Edit, fieldToFocus: 'bli_id' }
          }));
        }
        // Return old row if available, otherwise reject promise to prevent save
        if (oldRow) {
          return oldRow;
        }
        return Promise.reject(new Error('Selected BOQ line item is invalid'));
      }

      if (Number.isNaN(numericUnitPrice)) {
        showNotification('Unit price is required and must be a valid number', 'error');
        if (rowId) {
          setRateSheetItemRowModesModel((prev) => ({
            ...(prev || {}),
            [rowId]: { mode: GridRowModes.Edit, fieldToFocus: 'unit_price' }
          }));
        }
        if (oldRow) {
          return oldRow;
        }
        return Promise.reject(new Error('Unit price is required'));
      }

      if (numericUnitPrice < 0) {
        showNotification('Unit price must be non-negative', 'error');
        if (rowId) {
          setRateSheetItemRowModesModel((prev) => ({
            ...(prev || {}),
            [rowId]: { mode: GridRowModes.Edit, fieldToFocus: 'unit_price' }
          }));
        }
        if (oldRow) {
          return oldRow;
        }
        return Promise.reject(new Error('Unit price must be non-negative'));
      }

      const payload = {
        bli_id: Number(resolvedBliId),
        unit_price: numericUnitPrice,
        notes: trimmedNotes
      };

      console.log('📤 Sending payload:', payload);

      try {
        if (isDraftRow) {
          console.log('➕ Creating new rate sheet item');
          const response = await fetch(
            apiUrl(`/api/rate-sheets/${selectedRateSheet.rate_sheet_id}/items`),
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            }
          );

          console.log('📥 Create response status:', response.status);

          if (!response.ok) {
            const errorData = await response.json();
            console.log('❌ Create failed:', errorData);
            throw new Error(errorData.error || 'Failed to create rate item');
          }

          const saved = await response.json();
          console.log('✅ Created item:', saved);
          const normalized = {
            ...saved,
            id: saved.rsi_id,
            bli_id: resolvedBliId,
            unit_price:
              saved.unit_price !== undefined && saved.unit_price !== null
                ? Number(saved.unit_price)
                : numericUnitPrice,
            notes: saved.notes ?? trimmedNotes,
            isDraft: false
          };

          console.log('🔄 Updating state with normalized item:', normalized);
          setRateSheetItems((prev) => {
            const others = prev.filter((row) => row.id !== normalized.id);
            const newState = [...others, normalized];
            console.log('📝 Updated rateSheetItems:', newState);
            return newState;
          });
          if (rowId) {
            console.log('🧹 Cleaning up draft item:', rowId);
            setRateSheetDraftItems((prev) => {
              const filtered = prev.filter((row) => row.id !== rowId);
              console.log('📝 Updated rateSheetDraftItems:', filtered);
              return filtered;
            });
            setRateSheetItemRowModesModel((prev) => {
              const previous = (prev || {}) as Record<string, any>;
              const { [String(rowId)]: _ignored, ...rest } = previous;
              console.log('📝 Updated rowModesModel:', rest);
              return rest;
            });
          }
          showNotification('Rate item created successfully');
          console.log('✅ processRateSheetItemRowUpdate completed successfully');
          return normalized;
        } else {
          console.log('✏️ Updating existing rate sheet item');
          const response = await fetch(apiUrl(`/api/rate-sheet-items/${newRow.rsi_id}`), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });

          console.log('📥 Update response status:', response.status);

          if (!response.ok) {
            const errorData = await response.json();
            console.log('❌ Update failed:', errorData);
            throw new Error(errorData.error || 'Failed to update rate item');
          }

          const saved = await response.json();
          const normalized = {
            ...saved,
            id: saved.rsi_id,
            bli_id: saved.bli_id ? String(saved.bli_id) : resolvedBliId,
            unit_price:
              saved.unit_price !== undefined && saved.unit_price !== null
                ? Number(saved.unit_price)
                : numericUnitPrice,
            notes: saved.notes ?? trimmedNotes,
            isDraft: false
          };

          setRateSheetItems((prev) =>
            prev.map((row) => (row.id === normalized.id ? normalized : row))
          );
          showNotification('Rate item updated successfully');
          return normalized;
        }
      } catch (error) {
        console.error('❌ Error saving rate sheet item:', error);
        console.error('❌ Error stack:', error.stack);
        showNotification(error.message, 'error');
        throw error;
      }
    },
    [budgetLineItemMap, readOnly, selectedRateSheet, showNotification]
  );

  // All available BOQ line item options
  const allBudgetLineItemOptions = useMemo(
    () =>
      Array.isArray(budgetLineItems)
        ? budgetLineItems
            .filter(item => item && item.bli_id !== undefined && item.bli_id !== null)
            .map((item) => ({
              value: String(item.bli_id),
              label: item.item_code
                ? `${item.item_code} - ${item.item_description}`
                : item.item_description || `Item ${item.bli_id}`
            }))
        : [],
    [budgetLineItems]
  );

  // Get filtered options that exclude already assigned items in current rate sheet
  const getAvailableBudgetLineItemOptions = useCallback((currentRowId) => {
    if (!selectedRateSheet) return allBudgetLineItemOptions;
    
    // Get all assigned bli_ids from existing rate sheet items (excluding current row being edited)
    const assignedBliIds = new Set();
    
    // Add from existing saved items
    rateSheetItems.forEach(item => {
      if (item && item.bli_id && item.bli_id.trim() !== '') {
        assignedBliIds.add(String(item.bli_id));
      }
    });
    
    // Add from draft items (excluding current row being edited)
    rateSheetDraftItems.forEach(item => {
      if (item && item.id !== currentRowId && item.bli_id && item.bli_id.trim() !== '') {
        assignedBliIds.add(String(item.bli_id));
      }
    });
    
    // Filter out already assigned items
    return allBudgetLineItemOptions.filter(option => !assignedBliIds.has(option.value));
  }, [allBudgetLineItemOptions, selectedRateSheet, rateSheetItems, rateSheetDraftItems]);

  const budgetLineItemLabelMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(allBudgetLineItemOptions)) {
      allBudgetLineItemOptions.forEach((option) => {
        if (option && option.value && option.label) {
          map.set(option.value, option.label);
        }
      });
    }
    return map;
  }, [allBudgetLineItemOptions]);

  const rateSheetItemRows = useMemo(
    () => {
      const validItems = Array.isArray(rateSheetItems) 
        ? rateSheetItems.filter(item => 
            item && 
            item.id !== undefined && 
            item.id !== null &&
            typeof item === 'object'
          ) 
        : [];
      
      const validDrafts = Array.isArray(rateSheetDraftItems) 
        ? rateSheetDraftItems.filter(item => 
            item && 
            item.id !== undefined && 
            item.id !== null &&
            typeof item === 'object'
          ) 
        : [];
      
      const combinedRows = [...validItems, ...validDrafts];
      console.log('🔍 Rate sheet item rows calculation:', {
        rateSheetItems,
        rateSheetDraftItems,
        validItems,
        validDrafts,
        combinedRows
      });
      
      return combinedRows;
    },
    [rateSheetDraftItems, rateSheetItems]
  );

  useEffect(() => {
    const validIds = new Set(
      rateSheetItemRows
        .filter(row => row && row.id !== undefined && row.id !== null)
        .map(row => String(row.id))
    );

    setRateSheetItemRowModesModel(prev => {
      const previous = prev || {};
      let changed = false;
      const next = {};

      // Ensure all entries in previous are valid objects
      for (const [id, config] of Object.entries(previous)) {
        if (validIds.has(id) && config && typeof config === 'object') {
          next[id] = { ...config }; // Create a clean copy
        } else {
          changed = true;
        }
      }

      if (!readOnly && rateSheetItems.length === 0 && rateSheetDraftItems.length === 1) {
        const firstDraft = rateSheetDraftItems[0];
        if (firstDraft && firstDraft.id !== undefined && firstDraft.id !== null) {
          const draftId = String(firstDraft.id);
          if (!next[draftId]) {
            next[draftId] = { mode: GridRowModes.Edit, fieldToFocus: 'bli_id' };
            changed = true;
          }
        }
      }

      if (changed || Object.keys(next).length !== Object.keys(previous).length) {
        return next;
      }
      return previous;
    });
  }, [rateSheetDraftItems, rateSheetItemRows, rateSheetItems.length, readOnly]);

  // Rate Sheet columns
  const rateSheetColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'rate_sheet_id',
        headerName: 'ID',
        width: 90,
        type: 'number'
      },
      {
        field: 'sheet_name',
        headerName: 'Sheet Name',
        flex: 2,
        editable: !readOnly,
        minWidth: 200
      },
      {
        field: 'currency_code',
        headerName: 'Currency',
        width: 100,
        editable: !readOnly
      },
      {
        field: 'project_scope',
        headerName: 'Scope',
        width: 120,
        valueGetter: (params: any) => {
          if (!params || !params.row) return 'Global';
          return params.row.project_id ? 'Project' : 'Global';
        }
      },
      {
        field: 'is_active',
        headerName: 'Active',
        width: 100,
        type: 'boolean',
        editable: !readOnly,
        renderCell: (params) => (
          <Chip
            label={params.value ? 'Active' : 'Inactive'}
            color={params.value ? 'success' : 'default'}
            size="small"
            onClick={(e) => e.stopPropagation()}
          />
        )
      },
      {
        field: 'valid_from',
        headerName: 'Valid From',
        width: 150,
        type: 'date',
        valueGetter: (value: any) => {
          if (!value) return null;
          try {
            return new Date(value);
          } catch (e) {
            return null;
          }
        }
      },
      {
        field: 'valid_to',
        headerName: 'Valid To',
        width: 150,
        type: 'date',
        valueGetter: (value: any) => {
          if (!value) return null;
          try {
            return new Date(value);
          } catch (e) {
            return null;
          }
        }
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: readOnly ? 100 : 200,
        getActions: ({ id }) => {
          if (readOnly) {
            return [];
          }

          const isEditing = rateSheetRowModesModel[id]?.mode === GridRowModes.Edit;

          if (isEditing) {
            return [
              <GridActionsCellItem
                icon={<Tooltip title="Save"><SaveIcon /></Tooltip>}
                label="Save"
                onClick={handleRateSheetSaveClick(id)}
                showInMenu={false}
              />,
              <GridActionsCellItem
                icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>}
                label="Cancel"
                onClick={handleRateSheetCancelClick(id)}
                showInMenu={false}
              />
            ];
          }

          const isDuplicating = duplicatingRateSheetId === id;

          return [
            <GridActionsCellItem
              icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
              label="Edit"
              onClick={handleRateSheetEditClick(id)}
              disabled={isDuplicating}
              showInMenu={false}
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Duplicate">{isDuplicating ? <CircularProgress size={20} /> : <ContentCopyIcon />}</Tooltip>}
              label="Duplicate"
              onClick={handleDuplicateRateSheet(id)}
              disabled={isDuplicating}
              showInMenu={false}
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>}
              label="Delete"
              onClick={handleDeleteRateSheetClick(id)}
              disabled={isDuplicating}
              showInMenu={false}
            />
          ];
        }
      }
    ],
    [
      handleDeleteRateSheetClick,
      handleDuplicateRateSheet,
      handleRateSheetCancelClick,
      handleRateSheetEditClick,
      handleRateSheetSaveClick,
      rateSheetRowModesModel,
      readOnly,
      duplicatingRateSheetId
    ]
  );

  // Rate Sheet Items columns
  const rateItemColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'bli_id',
        headerName: 'BOQ Line Item',
        flex: 2,
        minWidth: 220,
        editable: !readOnly,
        type: 'singleSelect',
        valueOptions: (params) => {
          // Get available options for this specific row
          const currentRowId = params.row?.id;
          const availableOptions = getAvailableBudgetLineItemOptions(currentRowId);
          
          // If the current row already has a value, make sure it's included in options
          // (so user can see their current selection even if it would normally be filtered out)
          const currentValue = params.row?.bli_id;
          if (currentValue && currentValue.trim() !== '') {
            const hasCurrentValue = availableOptions.some(opt => opt.value === String(currentValue));
            if (!hasCurrentValue) {
              // Find the current value in all options and add it
              const currentOption = allBudgetLineItemOptions.find(opt => opt.value === String(currentValue));
              if (currentOption) {
                availableOptions.push(currentOption);
              }
            }
          }
          
          return availableOptions;
        },
        valueFormatter: (value, row) => {
          if (value === undefined || value === null || value === '') {
            return '';
          }
          const label = budgetLineItemLabelMap.get(String(value)) || '';
          return label;
        },

      },
      {
        field: 'unit_price',
        headerName: 'Unit Price',
        width: 140,
        type: 'number',
        editable: !readOnly,
        valueGetter: (value, row) => {
          if (!row) return null;
          const unitPrice = row.unit_price;
          return unitPrice === null || unitPrice === undefined ? null : Number(unitPrice);
        },

        valueFormatter: (value, row) => {
          if (value === undefined || value === null) {
            return '';
          }
          const numericValue = Number(value);
          if (isNaN(numericValue)) {
            return '';
          }
          return formatCurrency(numericValue, '', companyData.number_format).replace(/^[A-Z]{3}\s*/, '').replace(/^\$\s*/, '$');
        }
      },
      {
        field: 'notes',
        headerName: 'Notes',
        flex: 1,
        minWidth: 160,
        editable: !readOnly,
        valueGetter: (value, row) => {
          if (!row) return '';
          const notes = row.notes;
          return notes === null || notes === undefined ? '' : String(notes);
        },

      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: readOnly ? 80 : 150,
        getActions: ({ id, row }) => {
          if (readOnly) {
            return [];
          }

          const isEditing = rateSheetItemRowModesModel[id]?.mode === GridRowModes.Edit;
          const isDraft = row.isDraft || !row.rsi_id;

          if (isEditing) {
            return [
              <GridActionsCellItem
                icon={<Tooltip title="Save"><SaveIcon /></Tooltip>}
                label="Save"
                onClick={handleRateSheetItemSaveClick(id)}
                showInMenu={false}
              />,
              <GridActionsCellItem
                icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>}
                label="Cancel"
                onClick={handleRateSheetItemCancelClick(id, isDraft)}
                showInMenu={false}
              />
            ];
          }

          const actions = [
            <GridActionsCellItem
              icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
              label="Edit"
              onClick={handleRateSheetItemEditClick(id)}
              showInMenu={false}
            />
          ];

          if (!isDraft) {
            actions.push(
              <GridActionsCellItem
                icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>}
                label="Delete"
                onClick={handleDeleteRateItemClick(id)}
                showInMenu={false}
              />
            );
          }

          return actions;
        }
      },
    ],
    [
      allBudgetLineItemOptions,
      budgetLineItemLabelMap,
      getAvailableBudgetLineItemOptions,
      handleAddDraftItemRow,
      handleDeleteRateItemClick,
      handleRateSheetItemCancelClick,
      handleRateSheetItemEditClick,
      handleRateSheetItemSaveClick,
      rateSheetItemRowModesModel,
      readOnly
    ]
  );

  if (access === 'none') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Manage unit rates for BOQ line items through rate sheets. Create project-specific or global rate sheets
          to standardize pricing across contracts and estimates.
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label={`Rate Sheets (${rateSheets.length})`} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {!readOnly && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Create New Rate Sheet
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      fullWidth
                      label="Sheet Name"
                      value={newRateSheet.sheet_name}
                      onChange={(e) => setNewRateSheet({ ...newRateSheet, sheet_name: e.target.value })}
                      placeholder="e.g., 2024 Q1 Rates"
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <TextField
                      fullWidth
                      label="Currency"
                      value={newRateSheet.currency_code}
                      onChange={(e) => setNewRateSheet({ ...newRateSheet, currency_code: e.target.value })}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <TextField
                      fullWidth
                      label="Valid From"
                      type="date"
                      value={newRateSheet.valid_from ? newRateSheet.valid_from.toISOString().split('T')[0] : ''}
                      onChange={(e) => setNewRateSheet({ 
                        ...newRateSheet, 
                        valid_from: e.target.value ? new Date(e.target.value) : null 
                      })}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <TextField
                      fullWidth
                      label="Valid To"
                      type="date"
                      value={newRateSheet.valid_to ? newRateSheet.valid_to.toISOString().split('T')[0] : ''}
                      onChange={(e) => setNewRateSheet({ 
                        ...newRateSheet, 
                        valid_to: e.target.value ? new Date(e.target.value) : null 
                      })}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleAddRateSheet}
                      disabled={!newRateSheet.sheet_name.trim()}
                      startIcon={<AddIcon />}
                    >
                      Create Sheet
                    </Button>
                  </Grid>
                </Grid>
                <Grid container sx={{ mt: 2 }}>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newRateSheet.is_active}
                          onChange={(e) => setNewRateSheet({ ...newRateSheet, is_active: e.target.checked })}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent sx={{ p: 0 }}>
              <DataGrid
                rows={rateSheets}
                columns={rateSheetColumns}
                getRowId={(row) => row.rate_sheet_id}
                loading={loading}
                onRowClick={(params) => {
                  console.log('🔍 Row click:', params.row);
                  handleSelectRateSheet(params.row || null);
                }}
                editMode="row"
                rowModesModel={rateSheetRowModesModel || {}}
                onRowModesModelChange={handleRateSheetRowModesModelChange}
                onRowEditStop={handleRateSheetRowEditStop}
                processRowUpdate={processRateSheetRowUpdate}
                onProcessRowUpdateError={handleRateSheetProcessRowUpdateError}
                autoHeight
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 25 }
                  }
                }}
                pageSizeOptions={[25, 50, 100]}
                sx={{
                  border: 0,
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* Import/Export Buttons for Rate Sheets */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={handleDownloadTemplate}>
              Export Template
            </Button>
            <Button variant="contained" onClick={handleExport}>
              Export To Excel
            </Button>
            <Button variant="contained" onClick={handleImportClick} disabled={readOnly}>
              Import From Excel
            </Button>
            {canDeleteAll && (
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteAllClick}
                disabled={deletingAll || loading}
              >
                {deletingAll ? 'Deleting...' : 'Delete All'}
              </Button>
            )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
            />
          </Box>
        </TabPanel>

        <Card sx={{ mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Rate Sheet Items
            </Typography>

            {selectedRateSheet ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">
                    {selectedRateSheet.sheet_name}
                  </Typography>
                  {!readOnly && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddDraftItemRow}
                      disabled={!selectedRateSheet}
                    >
                      Add Item
                    </Button>
                  )}
                </Box>

                <DataGrid
                  rows={rateSheetItemRows.filter(row => {
                    const isValid = row && typeof row === 'object' && row.id !== undefined && row.id !== null;
                    if (!isValid) {
                      console.warn('❌ Filtering out invalid row:', row);
                    }
                    return isValid;
                  })}
                  columns={rateItemColumns}
                  getRowId={(row) => {
                    if (!row || typeof row !== 'object' || row.id === undefined || row.id === null) {
                      console.warn('❌ DataGrid received invalid row in getRowId:', typeof row, row);
                      return `invalid-row-${Math.random()}`;
                    }
                    return row.id;
                  }}
                  loading={itemsLoading}
                  editMode="row"
                  rowModesModel={(() => {
                    const model = rateSheetItemRowModesModel || {};
                    // Ensure all values in the model are valid objects
                    const cleanModel: any = {};
                    for (const [key, value] of Object.entries(model)) {
                      if (value && typeof value === 'object' && 'mode' in value) {
                        cleanModel[key] = { ...value };
                      }
                    }
                    return cleanModel;
                  })()}
                  onRowModesModelChange={handleRateSheetItemRowModesModelChange}
                  onRowEditStop={handleRateSheetItemRowEditStop}
                  processRowUpdate={processRateSheetItemRowUpdate}
                  onProcessRowUpdateError={handleRateSheetItemProcessRowUpdateError}
                  autoHeight
                  initialState={{
                    pagination: {
                      paginationModel: { page: 0, pageSize: 10 }
                    }
                  }}
                  pageSizeOptions={[10, 25, 50]}
                  sx={{
                    border: 0,
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none'
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: 'action.hover'
                    },
                    '& .MuiSelect-select': {
                      color: 'text.primary'
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary'
                    }
                  }}
                />

                {/* Import/Export Buttons for Rate Sheet Items */}
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" onClick={handleDownloadItemsTemplate} disabled={!selectedRateSheet}>
                    Export Template
                  </Button>
                  <Button variant="contained" onClick={handleExportItems} disabled={!selectedRateSheet}>
                    Export To Excel
                  </Button>
                  <Button variant="contained" onClick={handleImportItemsClick} disabled={readOnly || !selectedRateSheet}>
                    Import From Excel
                  </Button>
                  {canDeleteAllItems && (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleDeleteAllItemsClick}
                      disabled={deletingAllItems || itemsLoading || !selectedRateSheet}
                    >
                      {deletingAllItems ? 'Deleting...' : 'Delete All Items'}
                    </Button>
                  )}
                  <input
                    type="file"
                    ref={fileInputItemsRef}
                    onChange={handleFileItemsChange}
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                  />
                </Box>
              </>
            ) : (
              <Alert severity="info">
                Select a rate sheet above to view and manage its rate items.
              </Alert>
            )}
          </CardContent>
        </Card>

        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={() => setNotification({ ...notification, open: false })}
        >
          <Alert
            onClose={() => setNotification({ ...notification, open: false })}
            severity={notification.severity as AlertColor}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>

        {/* Delete Rate Sheet Confirmation Dialog */}
        <Dialog
          open={deleteRateSheetDialogOpen}
          onClose={handleDeleteRateSheetCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
            <Typography variant="h6" fontWeight={600} color="error">
              Delete Rate Sheet
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action cannot be undone! All rate items in this sheet will also be deleted.
            </Alert>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete the following rate sheet?
            </Typography>
            {rateSheetToDelete && (
              <Box sx={{
                bgcolor: 'grey.50',
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: 'grey.300'
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Sheet Name
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 1.5 }}>
                  {rateSheetToDelete.sheet_name}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Currency
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 1.5 }}>
                  {rateSheetToDelete.currency_code}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Scope
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {rateSheetToDelete.project_id ? 'Project' : 'Global'}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={handleDeleteRateSheetCancel} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteRateSheetConfirm}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
            >
              Delete Sheet
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Rate Item Confirmation Dialog */}
        <Dialog
          open={deleteRateItemDialogOpen}
          onClose={handleDeleteRateItemCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
            <Typography variant="h6" fontWeight={600} color="error">
              Delete Rate Item
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action cannot be undone!
            </Alert>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete the following rate item?
            </Typography>
            {rateItemToDelete && (
              <Box sx={{
                bgcolor: 'grey.50',
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: 'grey.300'
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  BOQ Line Item
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 1.5 }}>
                  {rateItemToDelete.item_description || 'N/A'}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Unit Price
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatCurrency(rateItemToDelete.unit_price || 0, '', companyData.number_format).replace(/^[A-Z]{3}\s*/, '').replace(/^\$\s*/, '$')}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={handleDeleteRateItemCancel} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteRateItemConfirm}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
            >
              Delete Item
            </Button>
          </DialogActions>
        </Dialog>

        {/* Import Source Dialog */}
        <Dialog open={importSourceOpen} onClose={() => setImportSourceOpen(false)}>
          <DialogTitle>Import From Excel</DialogTitle>
          <DialogContent>Choose source type:</DialogContent>
          <DialogActions>
            <Button onClick={() => handleImportSource(false)}>Template</Button>
            <Button onClick={() => handleImportSource(true)}>Third-party (Map Columns)</Button>
            <Button onClick={() => setImportSourceOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* Column Mapping Dialog */}
        <Dialog open={mappingOpen} onClose={() => { setMappingOpen(false); setFileHeaders([]); setMapFile(null); setMapping({}); }} maxWidth="md" fullWidth>
          <DialogTitle>Map Columns (Third-party Excel)</DialogTitle>
          <DialogContent>
            <div style={{ marginBottom: 8 }}>
              <input type="file" accept=".xlsx,.xls" onChange={handleMapFileChange} />
            </div>
            {!!fileHeaders.length && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {RATE_SHEET_FIELDS.map(f => (
                  <div key={f}>
                    <div style={{ fontSize: 12, color: '#555' }}>{f}</div>
                    <TextField select size="small" fullWidth value={mapping[f] || ''} onChange={(e) => setMapping(prev => ({ ...prev, [f]: e.target.value }))}>
                      <MenuItem value="">(none)</MenuItem>
                      {fileHeaders.map(h => <MenuItem key={`${f}-${h}`} value={h}>{h}</MenuItem>)}
                    </TextField>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMappingOpen(false)}>Cancel</Button>
            <Button onClick={handleMappingSubmit} disabled={!mapFile}>Import</Button>
          </DialogActions>
        </Dialog>

        {/* Importing Progress Dialog */}
        <Dialog open={excelImporting}>
          <DialogTitle>Importing...</DialogTitle>
          <DialogContent sx={{ minWidth: 300 }}>
            <LinearProgress variant="determinate" value={importProgress} />
            <Typography sx={{ mt: 1 }} align="center">{importProgress}%</Typography>
          </DialogContent>
        </Dialog>

        {/* Import Summary Dialog */}
        <Dialog open={importSummaryOpen} onClose={() => setImportSummaryOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{importSummaryData?.error ? 'Import Failed' : 'Import Summary'}</DialogTitle>
          <DialogContent>
            {importSummaryData && (
              <Box>
                <Typography variant="body1" sx={{ mb: 1 }}>Total rate sheets processed: <strong>{importSummaryData.total}</strong></Typography>
                <Typography variant="body2" sx={{ color: 'green' }}>Added: {importSummaryData.added}</Typography>
                <Typography variant="body2" sx={{ color: 'goldenrod' }}>Overwritten: {importSummaryData.overwritten}</Typography>
                <Typography variant="body2" sx={{ color: 'grey' }}>Skipped: {importSummaryData.skipped}</Typography>
                <Typography variant="body2" sx={{ color: 'red', mb: 2 }}>Failed: {importSummaryData.failed}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total changes: {importSummaryData.added + importSummaryData.overwritten}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => downloadImportLog(importSummaryData)} variant="outlined">Download Import Log</Button>
            <Button onClick={() => setImportSummaryOpen(false)} variant="contained">Close</Button>
          </DialogActions>
        </Dialog>

        {/* Delete All Confirmation Dialog */}
        <Dialog
          open={deleteAllDialogOpen}
          onClose={handleDeleteAllCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
            <Typography variant="h6" fontWeight={600} color="error">
              Delete All Rate Sheets
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action cannot be undone!
            </Alert>
            <Typography variant="body1">
              Are you sure you want to delete <strong>ALL</strong> rate sheets{projectId ? ' for this project' : ''}?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This will permanently remove all rate sheets and their associated rate items from the database.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={handleDeleteAllCancel} variant="outlined" disabled={deletingAll}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAllConfirm}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={deletingAll}
            >
              {deletingAll ? 'Deleting...' : 'Delete All'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Items Import Source Dialog */}
        <Dialog open={importItemsSourceOpen} onClose={() => setImportItemsSourceOpen(false)}>
          <DialogTitle>Import From Excel</DialogTitle>
          <DialogContent>Choose source type:</DialogContent>
          <DialogActions>
            <Button onClick={() => handleImportItemsSource(false)}>Template</Button>
            <Button onClick={() => handleImportItemsSource(true)}>Third-party (Map Columns)</Button>
            <Button onClick={() => setImportItemsSourceOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* Items Column Mapping Dialog */}
        <Dialog open={mappingItemsOpen} onClose={() => { setMappingItemsOpen(false); setFileItemsHeaders([]); setMapItemsFile(null); setMappingItems({}); }} maxWidth="md" fullWidth>
          <DialogTitle>Map Columns (Third-party Excel)</DialogTitle>
          <DialogContent>
            <div style={{ marginBottom: 8 }}>
              <input type="file" accept=".xlsx,.xls" onChange={handleMapItemsFileChange} />
            </div>
            {!!fileItemsHeaders.length && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {RATE_SHEET_ITEM_FIELDS.map(f => (
                  <div key={f}>
                    <div style={{ fontSize: 12, color: '#555' }}>{f}</div>
                    <TextField select size="small" fullWidth value={mappingItems[f] || ''} onChange={(e) => setMappingItems(prev => ({ ...prev, [f]: e.target.value }))}>
                      <MenuItem value="">(none)</MenuItem>
                      {fileItemsHeaders.map(h => <MenuItem key={`${f}-${h}`} value={h}>{h}</MenuItem>)}
                    </TextField>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMappingItemsOpen(false)}>Cancel</Button>
            <Button onClick={handleMappingItemsSubmit} disabled={!mapItemsFile}>Import</Button>
          </DialogActions>
        </Dialog>

        {/* Items Importing Progress Dialog */}
        <Dialog open={excelItemsImporting}>
          <DialogTitle>Importing...</DialogTitle>
          <DialogContent sx={{ minWidth: 300 }}>
            <LinearProgress variant="determinate" value={importItemsProgress} />
            <Typography sx={{ mt: 1 }} align="center">{importItemsProgress}%</Typography>
          </DialogContent>
        </Dialog>

        {/* Items Import Summary Dialog */}
        <Dialog open={importItemsSummaryOpen} onClose={() => setImportItemsSummaryOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{importItemsSummaryData?.error ? 'Import Failed' : 'Import Summary'}</DialogTitle>
          <DialogContent>
            {importItemsSummaryData && (
              <Box>
                <Typography variant="body1" sx={{ mb: 1 }}>Total rate sheet items processed: <strong>{importItemsSummaryData.total}</strong></Typography>
                <Typography variant="body2" sx={{ color: 'green' }}>Added: {importItemsSummaryData.added}</Typography>
                <Typography variant="body2" sx={{ color: 'goldenrod' }}>Overwritten: {importItemsSummaryData.overwritten}</Typography>
                <Typography variant="body2" sx={{ color: 'grey' }}>Skipped: {importItemsSummaryData.skipped}</Typography>
                <Typography variant="body2" sx={{ color: 'red', mb: 2 }}>Failed: {importItemsSummaryData.failed}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total changes: {importItemsSummaryData.added + importItemsSummaryData.overwritten}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => downloadItemsImportLog(importItemsSummaryData)} variant="outlined">Download Import Log</Button>
            <Button onClick={() => setImportItemsSummaryOpen(false)} variant="contained">Close</Button>
          </DialogActions>
        </Dialog>

        {/* Delete All Items Confirmation Dialog */}
        <Dialog
          open={deleteAllItemsDialogOpen}
          onClose={handleDeleteAllItemsCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
            <Typography variant="h6" fontWeight={600} color="error">
              Delete All 
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action cannot be undone!
            </Alert>
            <Typography variant="body1">
              Are you sure you want to delete <strong>ALL</strong> items in the rate sheet "{selectedRateSheet?.sheet_name}"?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This will permanently remove all rate sheet items from the database.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={handleDeleteAllItemsCancel} variant="outlined" disabled={deletingAllItems}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAllItemsConfirm}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={deletingAllItems}
            >
              {deletingAllItems ? 'Deleting...' : 'Delete All Items'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
};

export default UnitRate;
